import { ChevronDown, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../ui/button.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu.tsx';

/**
 * A task page's filter, drawn like the record lists' filter chips (`FieldChip` in
 * record-table/quick-filters.tsx): outlined until it narrows the list, then filled with a remove
 * button. The task pages filter through the tasks API's own parameters, not filter conditions,
 * so they cannot use `QuickFilters` itself.
 */
export function TaskFilterChip({
  name,
  label,
  active,
  onClear,
  testId,
  children,
}: {
  /** The filter's name, for the remove button ("Remove Status filter"). */
  name: string;
  label: string;
  active: boolean;
  onClear: () => void;
  testId: string;
  /** The menu's items. */
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant={active ? 'default' : 'outline'}
            data-active={active}
            data-testid={testId}
            className={active ? 'max-w-64 rounded-r-none' : 'max-w-64'}
            title={label}
          >
            <span className="truncate">{label}</span>
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
      {active && (
        <Button
          size="icon-sm"
          variant="default"
          className="rounded-l-none"
          aria-label={`Remove ${name} filter`}
          onClick={onClear}
        >
          <X />
        </Button>
      )}
    </span>
  );
}

/** How many tasks the filters found, where a record list says how many rows it loaded. */
export function TaskCount({ count }: { count: number }) {
  return (
    <span className="text-muted-foreground text-sm" data-testid="task-count">
      {count.toLocaleString()} task{count === 1 ? '' : 's'}
    </span>
  );
}
